import store from "@/store";

/**
 * Carts (MOO / QR / KIOSK) live in localStorage under ORDER_INFO_<merchant>_<branch>
 * (ORDER_QUEUE_INFO_... for Queue) and are written from dozens of places, so instead of
 * touching every writer, localStorage.setItem/removeItem is wrapped once here: any write
 * to a cart key stamps <key>__UPDATED_AT, and a cart left untouched for CART_TTL_MS is
 * dropped silently: the customer must not be able to tell. So it goes with the reward and
 * promo picked for it (a stale promo would pop "unavailable" later), its notes, the pickup/delivery
 * date and time already past, but not with what the customer would see reset: the dining
 * option (MOO asks it again when missing), Pickup/Delivery choice, address and contact.
 *
 * sweepExpiredCarts() runs synchronously in main.js before the app mounts, so a customer
 * coming back after a long time never sees the old items. For a tab that stayed open, the
 * sweep also runs when the page becomes visible again, and the page is reloaded when its own
 * cart was dropped after it loaded - by this sweep or by another tab (a new QR scan opens a
 * new tab), since components still hold the old cart in memory. A dropped cart leaves
 * <key>__EXPIRED_AT for that (kept even when a new cart is written: the old tab must still
 * reload rather than write its old items back). Carts of other merchants/branches, and empty
 * carts, never reload the page. A tab that stays visible is checked only once nobody has
 * touched it for IDLE_MS, so a reload never lands in the middle of what the customer is doing
 * (a payment included).
 *
 * A cart with a PENDING_PAYMENT_TXN marker is never dropped: the customer is coming back
 * from the gateway and clearCartIfPendingPaymentPaid / the cancelled-payment flow still
 * need it - but only for the marker's own 30 minutes, an older one is ignored.
 */
const CART_PREFIXES = ['ORDER_INFO_', 'ORDER_QUEUE_INFO_'];
const UPDATED_AT_SUFFIX = '__UPDATED_AT';
const EXPIRED_AT_SUFFIX = '__EXPIRED_AT';
// Picked for that cart; same <merchant>_<branch> suffix as the cart key (see localStorageName).
const CART_RELATED_KEYS = ['SELECTED_REWARD', 'PROMO_CODE_APPLIED', 'KIOSK_PROMO_CODE_APPLIED', 'ORDER_NOTES'];
// MyCart's CART_FORM_STATE fields that are only good for that order.
const CART_FORM_DATED_FIELDS = ['pickupDetails', 'deliveryDate', 'deliveryTime'];
export const CART_TTL_MS = 24 * 60 * 60 * 1000;
const IDLE_MS = 5 * 60 * 1000;

const cartPrefixOf = (key) => {
  if (typeof key !== 'string' || key.endsWith(UPDATED_AT_SUFFIX) || key.endsWith(EXPIRED_AT_SUFFIX)) {
    return null;
  }
  // ORDER_QUEUE_INFO_ does not start with ORDER_INFO_, so the order here doesn't matter.
  return CART_PREFIXES.find((prefix) => key.startsWith(prefix)) || null;
};

// Same 30-minute window as clearCartIfPendingPaymentPaid: an older marker is left over from
// an abandoned payment and must not keep the cart alive.
const PENDING_PAYMENT_EXPIRY_MS = 30 * 60 * 1000;
const hasLivePendingPayment = (merchantBranch, now) => {
  try {
    const pending = JSON.parse(localStorage.getItem('PENDING_PAYMENT_TXN_' + merchantBranch));
    return !!pending && now - new Date(pending.created_at).getTime() <= PENDING_PAYMENT_EXPIRY_MS;
  } catch (e) {
    return false;
  }
};

// localStorageName(`CART_FORM_STATE_${cartName}`): keeps the rest of what the customer filled in.
const dropDatedFormFields = (formKey) => {
  const raw = localStorage.getItem(formKey);
  if (raw === null) {
    return;
  }
  let form;
  try {
    form = JSON.parse(raw);
  } catch (e) {
    form = null;
  }
  if (!form || typeof form !== 'object') {
    localStorage.removeItem(formKey);
    return;
  }
  CART_FORM_DATED_FIELDS.forEach((field) => delete form[field]);
  localStorage.setItem(formKey, JSON.stringify(form));
};

const hasItems = (raw) => {
  try {
    const cart = JSON.parse(raw);
    return Array.isArray(cart) ? cart.length > 0 : !!cart;
  } catch (e) {
    return false;
  }
};

const getAllKeys = () => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    keys.push(localStorage.key(i));
  }
  return keys;
};

export const installCartExpiry = () => {
  const proto = Storage.prototype;
  const originalSetItem = proto.setItem;
  const originalRemoveItem = proto.removeItem;

  proto.setItem = function (key, value) {
    originalSetItem.call(this, key, value);
    if (cartPrefixOf(key) && this === window.localStorage) {
      // The stamp is extra: if it can't be written (quota), the cart write above already
      // went through and the caller must not see an error it didn't get before.
      try {
        originalSetItem.call(this, key + UPDATED_AT_SUFFIX, String(Date.now()));
      } catch (e) {
        // No stamp = the clock restarts on the next sweep.
      }
    }
  };

  proto.removeItem = function (key) {
    originalRemoveItem.call(this, key);
    if (cartPrefixOf(key) && this === window.localStorage) {
      originalRemoveItem.call(this, key + UPDATED_AT_SUFFIX);
    }
  };
};

/**
 * Drops every cart not updated within CART_TTL_MS.
 */
export const sweepExpiredCarts = () => {
  const now = Date.now();
  let keys = [];
  try {
    keys = getAllKeys();
  } catch (e) {
    // localStorage unavailable (private mode etc.) - nothing to expire.
  }
  keys.forEach((key) => {
    const prefix = cartPrefixOf(key);
    if (!prefix) {
      return;
    }
    try {
      const merchantBranch = key.slice(prefix.length);
      if (hasLivePendingPayment(merchantBranch, now)) {
        return;
      }
      const updatedAt = Number(localStorage.getItem(key + UPDATED_AT_SUFFIX));
      if (!updatedAt || updatedAt > now) {
        // No stamp (cart saved before this was deployed) or one from a device clock that
        // was ahead: start its clock now rather than wiping it or keeping it forever.
        localStorage.setItem(key + UPDATED_AT_SUFFIX, String(now));
        return;
      }
      if (now - updatedAt <= CART_TTL_MS) {
        return;
      }
      const raw = localStorage.getItem(key);
      localStorage.removeItem(key);
      if (hasItems(raw)) {
        localStorage.setItem(key + EXPIRED_AT_SUFFIX, String(now));
      }
      // The normal and the queue cart share these keys (and MyCart's CART_FORM_STATE, always
      // named after the ORDER_INFO_ cart): leave them while the other cart is still alive.
      if (CART_PREFIXES.some((p) => localStorage.getItem(p + merchantBranch) !== null)) {
        return;
      }
      CART_RELATED_KEYS.forEach((name) => localStorage.removeItem(name + '_' + merchantBranch));
      dropDatedFormFields('CART_FORM_STATE_ORDER_INFO_' + merchantBranch + '_' + merchantBranch);
    } catch (e) {
      // Leave this cart as it is; the others are still checked.
    }
  });
};

let pageLoadedAt = 0;

const reloadIfCartExpired = () => {
  sweepExpiredCarts();
  try {
    const merchantBranch = store.state.merchantCode + '_' + store.state.branchCode;
    const expired = CART_PREFIXES.some(
      (prefix) => Number(localStorage.getItem(prefix + merchantBranch + EXPIRED_AT_SUFFIX)) > pageLoadedAt
    );
    if (expired) {
      // Hidden first: the old items must not show for a frame before the reload, which then
      // looks like the browser reloading a background tab.
      document.documentElement.style.visibility = 'hidden';
      window.location.reload();
    }
  } catch (e) {
    // localStorage unavailable - nothing was swept either.
  }
};

// Call after the startup sweep: what that sweep dropped was never rendered.
export const watchCartExpiry = () => {
  pageLoadedAt = Date.now();
  let lastActivityAt = pageLoadedAt;
  const markActive = () => {
    lastActivityAt = Date.now();
  };
  ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach((type) =>
    window.addEventListener(type, markActive, { capture: true, passive: true })
  );
  setInterval(() => {
    if (document.visibilityState === 'visible' && Date.now() - lastActivityAt >= IDLE_MS) {
      reloadIfCartExpired();
    }
  }, IDLE_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      reloadIfCartExpired();
    }
  });
  // Restored from the back/forward cache: no script re-runs, the old DOM comes back as is.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      reloadIfCartExpired();
    }
  });
};
